'use client';
import React from 'react';
import PhotoPicker, { PhotoPickerFile } from '@/components/common/photo-picker';
import PhotoList from '@/screen/feed/write/components/photo-list';
import StepNavigation from '@/screen/feed/write/components/step-navigation';
import styles from './styles.module.scss';
import { ChevronRight } from 'lucide-react';

interface FeedWriteStep1Props {
  photos: PhotoPickerFile[];
  setPhotos: (photos: PhotoPickerFile[]) => void;
  handleNext: () => void;
  setBottomFixedViewHeight: (height: number) => void;
}

/**
 * First step of feeds writing process - Photo selection and ordering
 */
const FeedWriteStep1: React.FC<FeedWriteStep1Props> = ({
  photos,
  setPhotos,
  handleNext,
  setBottomFixedViewHeight
}) => {
  const [isDragging, setIsDragging] = React.useState(false);

  // Handle photo deletion
  const handleDeletePhoto = (index: number) => {
    const newPhotos = [...photos];
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
  };

  // Handle photo reordering with updated sortOrder
  const handleReorder = (reorderedPhotos: PhotoPickerFile[]) => {
    // Update sortOrder based on new position
    const updatedPhotos = reorderedPhotos.map((photo, index) => ({
      ...photo,
      sortOrder: index
    }));
    setPhotos(updatedPhotos);
  };

  // Handle photo change with updated sortOrder
  const handlePhotoChange = (newPhotos: PhotoPickerFile[]) => {
    // Update sortOrder based on position
    const updatedPhotos = newPhotos.map((photo, index) => ({
      ...photo,
      sortOrder: index
    }));
    setPhotos(updatedPhotos);
  };

  return (
    <div className={styles.container}>
      <div className={styles.editorGrid}>
        {/* Photo picker component */}
        <PhotoPicker photos={photos} onChange={handlePhotoChange} />

        {/* Photo list with reordering */}
        {photos.length > 0 && (
          <PhotoList
            photos={photos}
            onReorder={handleReorder}
            onDelete={handleDeletePhoto}
            setIsDragging={setIsDragging}
          />
        )}
      </div>

      {/* Navigation buttons */}
      <StepNavigation 
        onNext={handleNext}
        isNextDisabled={photos.length === 0}
        nextLabel={<div className={styles.next}><div>다음</div><ChevronRight width={20} height={20}/></div>}
        onHeightChange={setBottomFixedViewHeight}
      />
    </div>
  );
};

export default FeedWriteStep1;
